import PropTypes from "prop-types";
import React from "react";

const fmtCurrency = (n) =>
  `$${Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const fmtSignedCurrency = (n) => `${n < 0 ? "-" : "+"}${fmtCurrency(n)}`;

// Mirrors the basis the presenter chose for the profit figure (P&L columns).
const PROFIT_LABELS = {
  actual: "Actual Profit (Loss)",
  estimate: "Estimated Profit",
  forecast: "Projected Profit",
};

const RevenueCard = ({ grossRevenue, forecastRevenue, projectedProfit, projectedProfitBasis, breakEvenTickets, breakEvenAchieved }) => {
  // Forecast/profit figures are omitted for roles denied :profit_and_loss; only
  // Gross Revenue renders in that case.
  const showForecast = forecastRevenue != null;
  const forecastDeltaPct = grossRevenue > 0
    ? Math.round(((forecastRevenue - grossRevenue) / grossRevenue) * 100)
    : null;
  const profitPositive = projectedProfit >= 0;

  return (
    <div className="d2-card" style={{ padding: "20px" }}>
      <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--sl-text-primary)", marginBottom: "20px" }}>
        Revenue
      </div>

      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "11px", color: "var(--sl-text-tertiary)", marginBottom: "2px" }}>Gross Revenue</div>
        <div style={{ fontSize: "24px", fontWeight: 700, lineHeight: 1.1 }}>{fmtCurrency(grossRevenue)}</div>
      </div>

      {showForecast && (
        <>
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "11px", color: "var(--sl-text-tertiary)", marginBottom: "2px" }}>Forecast Revenue</div>
            <div style={{ fontSize: "24px", fontWeight: 700, lineHeight: 1.1 }}>{fmtCurrency(forecastRevenue)}</div>
            {forecastDeltaPct != null && forecastDeltaPct !== 0 && (
              <div style={{ fontSize: "11px", color: forecastDeltaPct > 0 ? "var(--sl-text-success)" : "var(--sl-text-error)", fontWeight: 600, marginTop: "2px" }}>
                {forecastDeltaPct > 0 ? "+" : ""}{forecastDeltaPct}% vs current
              </div>
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "11px", color: "var(--sl-text-tertiary)", marginBottom: "2px" }}>Break-Even</div>
            <div style={{ fontSize: "24px", fontWeight: 700, lineHeight: 1.1 }}>
              {breakEvenTickets > 0 ? `${breakEvenTickets.toLocaleString()} tickets` : "—"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "3px" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: breakEvenAchieved ? "var(--sl-text-success)" : "var(--sl-text-tertiary)" }} />
              <span style={{ fontSize: "11px", color: "var(--sl-text-secondary)" }}>{breakEvenAchieved ? "Achieved" : "Not yet"}</span>
            </div>
          </div>

          <div
            className={profitPositive ? "ep-profit ep-profit-positive" : "ep-profit ep-profit-negative"}
            style={{
              background: profitPositive ? "var(--sl-bg-success-primary)" : "var(--sl-bg-error-primary)",
              border: "1px solid var(--sl-border-secondary)",
              borderRadius: "var(--sl-radius-md)",
              padding: "12px",
            }}
          >
            <div style={{ fontSize: "11px", color: "var(--sl-text-secondary)", marginBottom: "2px" }}>{PROFIT_LABELS[projectedProfitBasis] || PROFIT_LABELS.forecast}</div>
            <div style={{ fontSize: "22px", fontWeight: 700, color: profitPositive ? "var(--sl-text-success)" : "var(--sl-text-error)", lineHeight: 1.1 }}>
              {fmtSignedCurrency(projectedProfit)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

RevenueCard.propTypes = {
  grossRevenue: PropTypes.number.isRequired,
  forecastRevenue: PropTypes.number,
  projectedProfit: PropTypes.number,
  projectedProfitBasis: PropTypes.oneOf(["actual", "estimate", "forecast"]),
  breakEvenTickets: PropTypes.number,
  breakEvenAchieved: PropTypes.bool,
};

RevenueCard.defaultProps = {
  forecastRevenue: null,
  projectedProfit: null,
  projectedProfitBasis: "forecast",
  breakEvenTickets: 0,
  breakEvenAchieved: false,
};

export default RevenueCard;
